import styled, { css } from 'styled-components';
import { Button } from 'components/elements';
import { colors, fontSize, fontWeight, media, radius, sizes, spacer } from '../../settings/style';

export const PageWrapper = styled.div`
  min-height: 100vh;
  color: ${colors.grey900};
`;

export const PageLayout = styled.div`
  max-width: ${sizes.xxl};
  margin: 0 auto;
  padding: 28px ${spacer.xl} 42px;
`;

export const BackButton = styled.button`
  border: 0;
  background: transparent;
  color: ${colors.grey900};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  margin-bottom: 16px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
`;

export const HeroCard = styled.div<{ coverPhotoUrl?: string }>`
  min-height: 360px;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  background: ${({ coverPhotoUrl }) =>
    coverPhotoUrl
      ? `linear-gradient(180deg, rgba(10, 17, 35, 0.28) 0%, rgba(10, 17, 35, 0.75) 100%), url(${coverPhotoUrl})`
      : 'linear-gradient(180deg, #2f3b52 0%, #182230 100%)'};
  background-size: cover;
  background-position: center;
  padding: 24px 28px 28px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  box-shadow: 0 4px 16px rgba(16, 24, 40, 0.12);

  ${media.md`
    min-height: 220px;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-end;
    gap: 12px;
    padding: 20px;
  `}
`;

export const HeroInfo = styled.div`
  color: ${colors.white};
`;

export const HeroTitle = styled.h1`
  margin: 0;
  font-size: clamp(30px, 5vw, 44px);
  line-height: 1.08;
  font-weight: 500;
  font-family: 'Cormorant Garamond', Georgia, serif;
  text-shadow: 0 1px 12px rgba(0, 0, 0, 0.35);
`;

export const HeroMeta = styled.div`
  margin-top: 10px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.92);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
`;

/** Same pattern as single group hero (Edit / Manage). */
export const HeroActions = styled.div<{ centered?: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;

  ${media.md`
    width: 100%;
  `}

  /* Your page switches to "mobile layout" at xl (<=1200px),
   * so center actions across the same breakpoint range. */
  ${media.xl`
    width: 100%;
  `}
`;

/** Main join CTA overlayed at hero center (desktop). */
export const HeroCenteredJoin = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;

  /* On mobile layout, put the CTA at the top center (above HeroInfo). */
  ${media.xl`
    top: 24px;
    transform: translateX(-50%);
    width: 100%;
    display: flex;
    justify-content: center;
    padding: 0 16px;
  `}
`;

export const HeroActionButton = styled(Button)`
  border: 0 !important;
  border-radius: 10px;
  padding: 8px 14px;
  min-height: 40px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: ${colors.grey900} !important;
  background: rgba(255, 255, 255, 0.95) !important;
  box-shadow: none !important;

  &:hover,
  &:focus {
    background: #475467 !important;
    border-color: transparent !important;
    color: ${colors.white} !important;
  }
`;

const heroJoinCta = css`
  border: 0;
  border-radius: 10px;
  padding: 8px 14px;
  min-height: 40px;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
`;

/** External meeting link — matches HeroActionButton look. */
export const HeroActionLink = styled.a`
  ${heroJoinCta}
  cursor: pointer;
  color: ${colors.primary800};
  background: ${colors.yellow500};

  &:hover {
    background: ${colors.yellow400};
  }
  &:focus {
    box-shadow: 0 2px 0 rgb(146 199 222 / 10%);
  }
`;

/** Shown in place of HeroActionLink until the event is live. */
export const HeroJoinDisabled = styled.span`
  ${heroJoinCta}
  cursor: not-allowed;
  color: ${colors.white};
  background: rgba(71, 84, 103, 0.7);
`;

/** Two-column layout — use only above `xl` (see MobileGrid). */
export const MainGrid = styled.div`
  margin-top: 24px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 25%);
  gap: 24px;
  align-items: start;
`;

/** Single column: hero → about → details/attendees → album → related */
export const MobileGrid = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 0;
`;

export const LeftColumn = styled.div`
  min-width: 0;
`;

export const RightColumn = styled.aside`
  min-width: 0;
`;

export const SurfaceCard = styled.section`
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);

  &:not(:first-child) {
    margin-top: 16px;
  }
`;

export const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: 26px;
  line-height: 1.15;
  font-weight: 500;
  font-family: 'Cormorant Garamond', Georgia, serif;
  color: ${colors.grey900};
`;

export const RichText = styled.div`
  font-size: 14px;
  line-height: 1.65;
  color: #344054;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
`;

export const DividerCard = styled(SurfaceCard)`
  margin-top: 24px;
`;

export const SideTitle = styled.h4`
  margin: 0 0 14px;
  font-size: 22px;
  line-height: 1.15;
  font-weight: 500;
  font-family: 'Cormorant Garamond', Georgia, serif;
  color: ${colors.grey900};
`;

export const StatRows = styled.div`
  margin-top: 12px;
  padding-top: 4px;
`;

export const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 0;
  border-top: 1px solid #eaecf0;

  &:first-of-type {
    border-top: 0;
    padding-top: 6px;
  }
`;

export const DetailLabel = styled.span`
  font-size: 14px;
  color: #667085;
`;

export const DetailValue = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.grey900};
  text-align: right;
`;

/** Event Details card — reference layout */
export const EventDetailsCard = styled(SurfaceCard)`
  padding: 20px 18px;
`;

export const LocationBlock = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 18px;
`;

export const LocationPin = styled.div`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f2f4f7;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667085;
  font-size: 16px;
`;

export const LocationText = styled.div`
  min-width: 0;
  flex: 1;
`;

export const VenueName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.grey900};
  line-height: 1.35;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
`;

export const AddressLine = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #667085;
  line-height: 1.45;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
`;

/** Start/end times, shown inside the About card right under its title. */
export const DateTimeSection = styled.div`
  display: inline-block;
  max-width: 100%;
  margin-bottom: 16px;
`;

export const DateTimeRow = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 8px;
  margin-bottom: 4px;
`;

/** The organizer's timezone — same layout, toned down against the local reading. */
export const SecondaryDateTimeRow = styled(DateTimeRow)`
  margin-top: 12px;
`;

export const DatePill = styled.div`
  flex: 0 0 auto;
  min-width: 0;
  background: #f2f4f7;
  border-radius: 8px;
  padding: 8px 12px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.2);
`;

export const DatePillDate = styled.div`
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  color: ${colors.grey900};
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
`;

export const DatePillTime = styled.div`
  display: inline-block;
  margin-top: 4px;
  font-size: 12px;
  font-weight: 700;
  color: ${colors.grey900};
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 5px;
  padding: 2px 10px;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
`;

/** Muted variants of the pill parts, used for the organizer's row. */
export const SecondaryDatePill = styled(DatePill)`
  background: transparent;
  border: 1px solid #eaecf0;
  box-shadow: none;
  padding: 6px 10px;
`;

export const SecondaryDatePillDate = styled(DatePillDate)`
  font-size: 11px;
  color: #667085;
`;

export const SecondaryDatePillTime = styled(DatePillTime)`
  margin-top: 4px;
  font-size: 11px;
  color: #667085;
  background: #f9fafb;
`;

export const DateArrow = styled.span`
  flex-shrink: 0;
  align-self: center;
  font-size: 15px;
  font-weight: 700;
  color: #98a2b3;
  line-height: 1;
`;

export const DateTimeZoneNote = styled.div`
  margin-bottom: 8px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.04em;
  color: #98a2b3;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
`;

export const RegistrationPanel = styled.div`
  margin-top: 4px;
  padding: 16px;
  background: #f2f4f7;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.2);
  text-align: center;

  .ant-input-number {
    width: 100%;
    max-width: 160px;
  }

  .ant-btn-primary {
    width: 100%;
    border-radius: 10px;
    min-height: 44px;
    font-weight: 600;
    background: ${colors.grey900} !important;
    border-color: ${colors.grey900} !important;
    color: white;

    &:hover,
    &:focus {
      background: #475467 !important;
      border-color: #475467 !important;
      color: #fff !important;
    }
  }
`;

export const EventDetailsFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #eaecf0;
`;

export const ShareRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const ShareLabel = styled.span`
  font-weight: 600;
  color: ${colors.grey900};
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
`;

export const ShareIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  a {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #f2f4f7;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #667085;
    transition: background 0.2s ease, color 0.2s ease;

    &:hover {
      background: #e4e7ec;
      color: ${colors.grey900};
    }

    i {
      font-size: 14px;
    }
  }
`;

export const DonateButton = styled.button`
  border: 1px solid #d0d5dd;
  background: #fff;
  color: ${colors.grey900};
  font-weight: 600;
  border-radius: 10px;
  padding: 8px 18px;
  cursor: pointer;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;

  &:hover {
    border-color: #475467;
    color: #475467;
  }
`;

export const StyledHeadDetail = styled.div`
  display: inline-flex;
  flex-flow: column;
  justify-content: center;
  align-items: flex-end;
  padding: ${spacer.md} ${spacer.lg};
`;

export const StyledGuestWrap = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  margin-bottom: ${spacer.lg};
`;

export const GuestInvitationTitle = styled.p`
  text-align: center;
  font-weight: ${fontWeight.bold};
`;

export const VisitorInvitaionResponseTitle = styled.p`
  text-align: center;
  font-weight: ${fontWeight.bold};
  padding: ${spacer.md};
  .approved {
    color: ${colors.success};
  }
  .rejected {
    color: ${colors.danger};
  }
`;

export const StyledGuestText = styled.div<{ marginBottom?: string }>`
  display: inline-flex;
  white-space: nowrap;
  margin: auto;
  padding-bottom: ${({ marginBottom }) => marginBottom && spacer.md};
`;

export const StyledSeatsLeft = styled.div`
  display: block;
  white-space: nowrap;
  padding-bottom: ${spacer.sm};
`;

export const StyledGuestCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${colors.white};
  border-radius: ${radius.lg};
  height: 30px;
  min-width: 54px;
  margin-left: ${spacer.lg};
  padding: 0 ${spacer.sm};
  .ant-input-number {
    border: none;
    box-shadow: none;
    outline: none;
  }
`;

export const AttendingStatus = styled.div`
  font-size: ${fontSize.sm};
`;
